import { useLocale as useLocaleR42 } from '@/lib/locale';
import { useLocale as useLocaleR40 } from '@/lib/locale';
import React, { useEffect, useMemo, useState } from 'react';
import { CoachChip } from '@/components/CoachChip';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { WaveDivider } from '@/components/WaveDivider';
import { Logo } from '@/components/Logo';
import { PaymentProviderBadge } from '@/components/PaymentProviderBadge';
import { SocialIconLinks } from '@/components/SocialIconLinks';
import CustomSectionRenderer from '@/components/landing/CustomSectionRenderer';
import ContactCoachDialog from '@/components/ContactCoachDialog';
import { ClassFinderWizard } from '@/components/classfinder/ClassFinderWizard';
import { LocationCards } from '@/components/LocationCards';
import Seo from '@/components/Seo';
import { CheckCircle2, MapPin, Award, Star, Users, ArrowRight, ShieldCheck, Phone, Eye, LogIn, CalendarRange, Clock, Tent } from 'lucide-react';
import { fmtPrice } from '@/lib/api';
import { formatSlotSchedule, countSlotSessions } from '@/lib/packageSchedule';
import { getTheme, getFont, resolveTheme } from '@/lib/landingThemes';
import CoachSite from '@/components/coachsite/CoachSite';
import { AnnouncementPopup } from '@/components/AnnouncementPopup';
import { getSiteTheme } from '@/lib/siteThemes';
import { getSiteLayout } from '@/lib/siteLayouts';

const MULTIPAGE_SITE_PAGES = new Set(['about', 'programs', 'team', 'locations', 'contact']);

export default function CoachPublic() {
    const { t: t42 } = useLocaleR42();
    const { t: tr40 } = useLocaleR40();
    const { slug, sitePage } = useParams();
    const location = useLocation();
    // Preview mode: the editor opens /instructor/landing-page/preview in a new tab.
    // In that case we use the authenticated preview endpoint instead of the public one.
    const isPreview = location.pathname.startsWith('/instructor/landing-page/preview');
    // Phase 103b — "Preview as Parent" overlay loads the live public page in
    // an iframe with ?parent_preview=1. The page stays fully interactive
    // (wizard included) but checkout / apply navigation is blocked.
    const isParentPreview = new URLSearchParams(location.search).get('parent_preview') === '1';
    // Phase 113 — the visual editor hosts the preview in an iframe with
    // ?editor=1 (click-to-edit overlays) and ?page=<id> (which site page to
    // show). Public visitors get the page from the /coach/:slug/:sitePage
    // route param instead.
    const searchParams = new URLSearchParams(location.search);
    const isEditorMode = isPreview && searchParams.get('editor') === '1';
    // Phase 114 — "Compare layouts": the editor renders four scaled preview
    // iframes with ?compare=1&layout=<id>[&theme=<id>]. The overrides are
    // applied client-side to the fetched landing page so a coach can see
    // every layout with their real content before committing (unsaved).
    const isCompareMode = isPreview && searchParams.get('compare') === '1';
    const layoutOverride = isPreview ? (searchParams.get('layout') || '') : '';
    const themeOverride = isPreview ? (searchParams.get('theme') || '') : '';
    const previewPage = searchParams.get('page') || 'home';
    const publicPage = sitePage && MULTIPAGE_SITE_PAGES.has(sitePage) ? sitePage : 'home';
    const activeSitePage = isPreview ? previewPage : publicPage;
    const [rawData, setRawData] = useState(null);
    const [error, setError] = useState(null);
    // Live-draft preview — the Website Builder hosts this page in an iframe
    // and streams the coach's UNSAVED landing-page draft via postMessage
    // ({type:'sp-draft', landing_page}). We merge it over the server payload
    // so every edit shows instantly without a save. Only honoured in
    // preview mode when embedded (window.parent !== window) and same-origin.
    const embeddedPreview = isPreview && typeof window !== 'undefined' && window.parent && window.parent !== window;
    const [draftLp, setDraftLp] = useState(null);
    useEffect(() => {
        if (!embeddedPreview) return undefined;
        const onMessage = (e) => {
            if (e.origin !== window.location.origin) return;
            const msg = e.data || {};
            if (msg.type === 'sp-draft' && msg.landing_page && typeof msg.landing_page === 'object') {
                setDraftLp(msg.landing_page);
            }
        };
        window.addEventListener('message', onMessage);
        // Handshake: tell the editor we're mounted so it can push the current draft.
        try { window.parent.postMessage({ type: 'sp-ready' }, window.location.origin); } catch { /* ignore */ }
        return () => window.removeEventListener('message', onMessage);
    }, [embeddedPreview]);

    const data = useMemo(() => {
        if (!rawData || !draftLp || !embeddedPreview || rawData.paused) return rawData;
        const base = rawData.landing_page || {};
        const merged = {
            ...base,
            ...draftLp,
            // Server-shaped fields the draft must not clobber.
            announcement: base.announcement ?? null,
            site_team: {
                heading: draftLp.site_team?.heading || '',
                subheading: draftLp.site_team?.subheading || '',
                members: Array.isArray(draftLp.site_team?.members) ? draftLp.site_team.members : [],
            },
            // Compare tiles pin a layout/theme via the URL — keep that on top.
            ...(layoutOverride ? { site_layout: layoutOverride } : {}),
            ...(themeOverride ? { site_theme: themeOverride } : {}),
        };
        // Re-apply package visibility from the draft (server filtered by the SAVED values).
        let packages = Array.isArray(rawData.all_packages) ? rawData.all_packages : (rawData.packages || []);
        if (merged.show_packages === false) {
            packages = [];
        } else if (Array.isArray(merged.visible_package_ids) && merged.visible_package_ids.length) {
            const allow = new Set(merged.visible_package_ids);
            packages = packages.filter((p) => allow.has(p.id));
        }
        return { ...rawData, landing_page: merged, packages };
    }, [rawData, draftLp, embeddedPreview, layoutOverride, themeOverride]);
    // Phase 76g — Scott's swimmer bug: an already-approved parent should
    // NOT be routed through the /register apply form again. Sniff their
    // application status with this coach on mount so we can rebrand the
    // CTAs to "Enroll Now" and route them straight to the paid booking
    // flow. Silent fail — if this call errors (not logged in, network,
    // older backend) we fall back to the original apply flow.
    const [parentApp, setParentApp] = useState(null); // application row from /parent/applications for THIS coach
    // Phase 73j — Contact dialog state. Triggered by the hero "Contact Coach"
    // button; replaces the bare mailto:/tel: anchor which silently failed on
    // mobile devices without a configured email/dialer app.
    const [contactDialogOpen, setContactDialogOpen] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const path = isPreview
            ? '/instructor/landing-page/preview'
            : `/public/coach/${slug}`;
        api.get(path)
            .then(r => {
                const payload = r.data;
                if (isPreview && payload?.landing_page && (layoutOverride || themeOverride)) {
                    payload.landing_page = {
                        ...payload.landing_page,
                        ...(layoutOverride ? { site_layout: layoutOverride } : {}),
                        ...(themeOverride ? { site_theme: themeOverride } : {}),
                    };
                }
                // Phase 126 (SEO audit) — admin-configured slug redirect. When
                // an owner has two accounts (e.g. /coach/amanda + /coach/itsamanda)
                // the admin can point the duplicate at the canonical slug. We
                // replace the history entry so back-button behaviour stays sane
                // and bot pre-renderers see the destination page + its canonical.
                const redirectTo = !isPreview && payload?.seo?.redirect_to;
                if (redirectTo && redirectTo !== slug) {
                    const rest = location.pathname.replace(new RegExp(`^/coach/${slug}`), '');
                    navigate(`/coach/${redirectTo}${rest}${location.search || ''}`, { replace: true });
                    return;
                }
                setRawData(payload);
            })
            .catch(e => setError(e?.response?.data?.detail || (isPreview ? 'Could not load preview — make sure you are logged in.' : 'Coach not found')));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug, isPreview, layoutOverride, themeOverride]);

    // Phase 76g — Load the parent's application with this coach so we can
    // short-circuit the "apply again" flow. Runs alongside the coach-page
    // fetch; both silent-fail so the page always renders.
    useEffect(() => {
        if (isPreview) return;
        let cancelled = false;
        api.get('/parent/applications')
            .then((r) => {
                if (cancelled) return;
                const rows = Array.isArray(r.data) ? r.data : [];
                // Match by coach slug — the coach payload from /public/coach
                // includes slug so we can align on it without another call.
                const mine = rows.find((row) => row?.coach?.slug === slug);
                setParentApp(mine || null);
            })
            .catch(() => { /* not logged in / not a parent — ignore */ });
        return () => { cancelled = true; };
    }, [slug, isPreview]);

    const startRegisterRef = React.useRef(null);
    // Phase 137 — `?apply=1` deep link (Travel Smart offer accepted by a
    // new family): open the apply flow automatically once the page is up.
    useEffect(() => {
        if (!rawData || isPreview || isParentPreview) return;
        const params = new URLSearchParams(location.search);
        if (params.get('apply') !== '1') return;
        params.delete('apply');
        navigate({ pathname: location.pathname, search: params.toString() ? `?${params.toString()}` : '' }, { replace: true });
        const t = setTimeout(() => startRegisterRef.current?.(), 250);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rawData]);

    if (error) {
        return (
            <div className="min-h-dvh bg-[#f4f7fb] flex flex-col items-center justify-center p-6 text-center">
                <Logo />
                <h1 className="mt-6 font-display text-2xl font-bold text-[#0a1628]">{tr40("Page unavailable")}</h1>
                <p className="mt-2 text-[#1a3a5c]">{error}</p>
                <Button asChild className="sd-cta mt-6"><Link to="/">{tr40("Back home")}</Link></Button>
            </div>
        );
    }

    if (!data) {
        return <div className="p-6 max-w-3xl mx-auto space-y-3"><Skeleton className="h-12" /><Skeleton className="h-40" /><Skeleton className="h-40" /></div>;
    }

    // === Seasonal / admin pause short-circuit ============================
    // Backend returns a minimal payload with `paused: true` when the coach is
    // on Seasonal Hold (or paused by admin). We render a friendly "taking a
    // break" page with the coach's optional resume note INSTEAD of the normal
    // landing-page hero / pricing / Apply flow.
    if (data.paused) {
        const c = data.coach || {};
        const accent = c.primary_color || '#00d4ff';
        const isSeasonal = data.pause_reason === 'seasonal';
        const isBilling = data.pause_reason === 'billing';
        return (
            <div className="min-h-dvh sd-noise relative overflow-hidden bg-[#f4f7fb]" data-testid="coach-public-paused-state">
                <div className="sd-gradient-hero absolute inset-0 -z-10 opacity-60" />
                <div className="mx-auto flex min-h-dvh max-w-2xl flex-col items-center justify-center px-6 py-12 text-center">
                    {c.logo_url ? (
                        <img src={c.logo_url} alt={c.display_name} className="h-20 w-20 rounded-2xl object-cover shadow-md" />
                    ) : (
                        <Logo />
                    )}
                    <div
                        className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-semibold uppercase tracking-wider shadow-sm"
                        style={{ color: accent }}
                    >
                        {isSeasonal ? tr40("❄️ Off-season") : isBilling ? tr40("⏸ Temporarily unavailable") : tr40("⏸ Taking a short break")}
                    </div>
                    <h1 className="mt-4 font-display text-3xl font-bold text-[#0a1628] sm:text-4xl">
                        {isBilling ? `${c.display_name}'s booking page is temporarily unavailable` : `${c.display_name} is on a break`}
                    </h1>
                    <div className="mt-5 max-w-xl whitespace-pre-line rounded-2xl border border-[#1a3a5c]/10 bg-white px-6 py-5 text-[#1a3a5c] shadow-sm" data-testid="coach-public-paused-note">
                        {isBilling ? tr40("New bookings are paused for the moment. Please check back soon or contact the coach directly.") : data.season_resume_note}
                    </div>
                    <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                        <Button asChild variant="outline" className="h-11 rounded-xl border-2 border-[#0a1628] text-[#0a1628]" data-testid="coach-public-paused-login">
                            <Link to={`/login?from=${encodeURIComponent(`/coach/${slug}`)}`}>
                                <LogIn className="mr-1 h-4 w-4" /> {tr40("Returning parent? Log in")} </Link>
                        </Button>
                        <Button asChild className="sd-cta sd-focus h-11 rounded-xl" data-testid="coach-public-paused-find-other">
                            <Link to="/#find-coach">{tr40("Find another coach")}</Link>
                        </Button>
                    </div>
                    <p className="mt-8 text-xs text-[#1a3a5c]/60">{tr40("Powered by SplashPad · Superhero Swim University")}</p>
                </div>
            </div>
        );
    }

    const { coach, packages, programs, testimonials, faqs, landing_page } = data;
    const primary = coach.primary_color || '#00d4ff';
    const lp = landing_page; // null when not Pro / not enabled
    // Phase 104 — Class Finder moved into the private Parent Portal. Parents
    // must be APPROVED students before they can browse classes and check out,
    // so the wizard NEVER renders for real public visitors (any plan, even
    // with instant/auto-approval enabled). It still renders in the coach's
    // preview modes (landing-page editor preview + "Preview as Parent"
    // overlay) so coaches can validate the wizard — checkout/apply actions
    // are already blocked in preview.
    const hasClassFinder = Array.isArray(programs) && programs.length > 0 && packages.length > 0;
    const showWizardPreview = hasClassFinder && (isPreview || isParentPreview);

    // === Button-text + visibility overrides from the Pro landing page ===
    // Starter coaches don't have `lp`, so they get the historical defaults.
    //
    // Phase 76h+ — Correct label pathway (Scott clarification, Aug 2026):
    //   • Brand new visitor / no application → "Apply Now"
    //     (they must apply to become a student first).
    //   • Accepted student / approved application → "Enroll"
    //     (they're already a student, just picking a class).
    //   • Pending application → "Application under review" (disabled).
    //
    // Earlier attempt made "Enroll" universal, which correctly served
    // returning approved parents (Scott's original complaint) but
    // misled first-timers into thinking they could skip the student
    // application step. This version keeps the clean two-state model.
    const ctaApplyText = !lp?.cta_text || lp.cta_text === 'Apply Now' ? tr40('Apply Now') : lp.cta_text;
    const contactText = !lp?.contact_button_text || lp.contact_button_text === 'Contact Coach' ? tr40('Contact Coach') : lp.contact_button_text;
    const contactVisible = lp ? (lp.contact_button_visible !== false) : true;
    // Strip everything but digits + leading + for the tel: href (iOS Safari is strict)
    const contactPhoneClean = (lp?.contact_phone || '').replace(/[^\d+]/g, '');

    // Phase 76g/h — If we detected an existing application for the logged-in
    // parent, the CTAs shift meaning:
    //   * approved / awaiting_payment → "Enroll" straight into the
    //     approved-parent booking page (skips the apply form entirely).
    //   * pending → "Application under review" (disabled, gentle info).
    //   * declined / dismissed / checkout_abandoned → treat as no app
    //     and show the normal apply CTA (they may want to re-apply).
    //   * anything else / no parentApp → normal apply flow.
    const alreadyApproved = parentApp && ['approved', 'awaiting_payment'].includes(parentApp.status);
    const applicationPending = parentApp && parentApp.status === 'pending';
    const enrollCtaText = alreadyApproved ? 'Enroll' : (applicationPending ? 'Application under review' : ctaApplyText);
    const enrollCtaDisabled = !!applicationPending;

    const startRegister = (pkgId) => {
        if (isPreview || isParentPreview) {
            // No-op in preview mode — coaches reviewing their page shouldn't be sent
            // into the parent registration flow by accident.
            return;
        }
        // Phase 76g — Skip the redundant apply form when the parent is
        // already approved. Route straight to the approved-parent
        // booking page so they can pick a package + pay in one step.
        if (alreadyApproved) {
            navigate(`/parent/coach/${slug}/book${pkgId ? `?pkg=${pkgId}` : ''}`);
            return;
        }
        if (applicationPending) {
            // No route change — a pending application means the coach
            // still needs to approve them. Toast handled elsewhere.
            return;
        }
        // Classic flow — parents apply first, no per-package CTA
        // (pkgId is intentionally ignored — application is package-agnostic).
        navigate(`/coach/${slug}/register`);
    };
    startRegisterRef.current = startRegister;

    // Per-coach SEO — shared by BOTH the legacy single-page renderer and the
    // Phase 113 multi-page website. Only emitted on the public route, not the
    // authenticated /instructor/landing-page/preview view. The title and
    // description prefer the Pro landing-page values when set, then fall
    // back to display_name + service area.
    const seoNode = !isPreview ? (() => {
        // SEO audit (Sept 2026): clamp titles to ≤60 chars so they don't
        // truncate in SERPs (e.g. /coach/splashauburn), and only emit
        // LocalBusiness when we can satisfy its REQUIRED address field —
        // otherwise fall back to a Service schema (no address required).
        const clampTitle = (t) => {
            if (t.length <= 60) return t;
            const cut = t.slice(0, 57);
            return `${cut.slice(0, Math.max(cut.lastIndexOf(' '), 40))}…`;
        };
        const coachName = coach.display_name || 'Swim Coach';
        const PAGE_LABELS = { about: 'About', programs: 'Programs & Pricing', locations: 'Locations', contact: 'Contact' };
        const pageLabel = activeSitePage !== 'home' ? PAGE_LABELS[activeSitePage] : null;
        const baseTitle = pageLabel
            ? `${pageLabel} | ${coachName}`
            : (lp?.hero_headline
                ? `${lp.hero_headline} | ${coachName}`
                : `${coachName} | Private swim lessons`);
        const pagePath = pageLabel ? `/${activeSitePage}` : '';
        const pageUrl = `https://splashpad.pro/coach/${slug}${pagePath}`;
        // Phase 126 (SEO audit) — never put a base64 data: URI into og:image /
        // JSON-LD `image`. A 600 KB inline hero on one Pro site was being
        // repeated 4x in the HTML (2.4 MB page) and flagged as an invalid
        // URL by structured-data validators. Legacy inline images are being
        // migrated to R2 server-side; until then fall back to a real URL.
        const isDataUri = (u) => typeof u === 'string' && /^data:/i.test(u.trim());
        const firstRealUrl = (...cands) => cands.find((u) => u && !isDataUri(u)) || 'https://splashpad.pro/splashpad-logo.png';
        const pageImage = firstRealUrl(lp?.hero_photo_url, coach.logo_url);
        const noIndex = !!(coach.seo_noindex || data?.seo?.noindex);
        const pageDesc = lp?.hero_subheadline
            ? lp.hero_subheadline.slice(0, 158)
            : `Book private swim lessons with ${coachName}${coach.city ? ` in ${coach.city}` : ''}. Apply online, sign your waiver, and get scheduled.`;
        const jsonLd = coach.city
            ? {
                '@context': 'https://schema.org',
                '@type': 'LocalBusiness',
                name: coachName,
                url: pageUrl,
                image: pageImage,
                description: pageDesc,
                priceRange: '$$',
                address: {
                    '@type': 'PostalAddress',
                    addressLocality: coach.city,
                    ...(coach.state ? { addressRegion: coach.state } : {}),
                    addressCountry: 'US',
                },
                ...(coach.contact_phone ? { telephone: coach.contact_phone } : {}),
                ...(coach.google_business_url ? { sameAs: [coach.google_business_url] } : {}),
            }
            : {
                '@context': 'https://schema.org',
                '@type': 'Service',
                name: `Private swim lessons with ${coachName}`,
                url: pageUrl,
                image: pageImage,
                description: pageDesc,
                serviceType: 'Private swim lessons',
                provider: { '@type': 'Person', name: coachName },
                ...(coach.google_business_url ? { sameAs: [coach.google_business_url] } : {}),
            };
        return (
            <Seo
                title={clampTitle(baseTitle)}
                description={pageDesc}
                canonical={pageUrl}
                image={pageImage}
                imageAlt={coachName}
                type="profile"
                noIndex={noIndex}
                jsonLd={jsonLd}
            />
        );
    })() : null;

    // Contact button visibility shared by legacy + multipage renderers.
    const contactAvailable = contactVisible && !!(contactPhoneClean || (coach.settings?.accept_inquiries && coach.contact_email));

    // === Phase 115 — Promo pop-up ==========================================
    // Server only ships `announcement` when it's enabled, Pro, targeted at the
    // website and not expired. Hidden inside the editor iframe / compare
    // tiles / parent-preview so it never blocks editing.
    const announcement = lp?.announcement || null;
    const showAnnouncement = !!announcement && !isEditorMode && !isCompareMode && !isParentPreview;
    const announcementNode = showAnnouncement ? (
        <AnnouncementPopup
            announcement={announcement}
            scope="site"
            coachKey={slug}
            coachName={coach.display_name}
            accentColor={primary}
            scopeClassName={(!!lp && lp.multipage_enabled !== false) ? 'csite csite-portal' : ''}
            scopeStyle={(!!lp && lp.multipage_enabled !== false) ? { ...getSiteTheme(lp).vars, ...(getSiteLayout(lp).vars || {}) } : undefined}
            actionCtx={{
                surface: 'website',
                slug,
                navigate,
                onRegister: (pkgId) => startRegister(pkgId),
                onPrograms: () => {
                    if (isPreview) return;
                    if (lp && lp.multipage_enabled !== false) navigate(`/coach/${slug}/programs`);
                    else document.getElementById('packages')?.scrollIntoView({ behavior: 'smooth' });
                },
            }}
        />
    ) : null;

    // === Phase 113 — Pro multi-page website ================================
    // Renders when the coach has an ACTIVE Pro landing page and hasn't opted
    // back into the classic single-page layout. Legacy rendering below stays
    // byte-identical for Starter coaches and multipage opt-outs.
    const multipageActive = !!lp && lp.multipage_enabled !== false;
    if (multipageActive) {
        const wizardPreviewNode = showWizardPreview ? (
            <div key="wizard-preview">
                <div className="bg-[#f4f7fb] pt-6" data-testid="classfinder-preview-note">
                    <div className="mx-auto max-w-6xl px-4 sm:px-6">
                        <div className="flex items-center gap-2 rounded-xl border border-[#ffd700]/50 bg-[#fff8d6] px-4 py-2.5 text-xs font-semibold text-[#0a1628]">
                            <Eye className="h-4 w-4 shrink-0" /> {tr40("Preview only — parents see this Class Finder inside their private portal after their application is approved. It never shows on your public page.")} </div>
                    </div>
                </div>
                <ClassFinderWizard
                    slug={slug}
                    coach={coach}
                    packages={packages}
                    programs={programs}
                    locations={coach.locations || []}
                    primary={primary}
                    previewMode={isPreview || isParentPreview}
                />
            </div>
        ) : null;
        return (
            <div className="min-h-dvh">
                {seoNode}
                {isPreview && !isEditorMode && !isCompareMode && (
                    <div className="relative z-50 flex items-center justify-between gap-3 border-b border-[#ffd700]/40 bg-[#fff8d6] px-4 py-2.5 text-xs sm:text-sm" data-testid="preview-mode-banner">
                        <div className="flex min-w-0 items-center gap-2 text-[#0a1628]">
                            <Eye className="h-4 w-4 shrink-0 text-[#0a1628]" />
                            <strong className="shrink-0">{tr40("Preview mode")}</strong>
                            <span className="hidden truncate text-[#1a3a5c]/80 sm:inline">{tr40("— this is exactly how parents will see your website. Apply and Book buttons are disabled here.")}</span>
                        </div>
                        <Button size="sm" variant="outline" className="h-8 shrink-0 rounded-lg" onClick={() => { try { window.close(); } catch { /* ignore */ } }} data-testid="preview-close-button"> {tr40("Close")} </Button>
                    </div>
                )}
                <CoachSite
                    data={data}
                    slug={slug}
                    page={activeSitePage}
                    isPreview={isPreview}
                    isParentPreview={isParentPreview}
                    isEditor={isEditorMode}
                    cta={{ text: enrollCtaText, disabled: enrollCtaDisabled, onApply: startRegister }}
                    contact={{ text: contactText, visible: contactAvailable, onContact: () => setContactDialogOpen(true) }}
                    wizardPreviewNode={wizardPreviewNode}
                />
                <ContactCoachDialog
                    open={contactDialogOpen}
                    onOpenChange={setContactDialogOpen}
                    coach={coach}
                    primary={primary}
                />
                {announcementNode}
            </div>
        );
    }

    return (
        <div className="min-h-dvh">
            {seoNode}
            {isPreview && (
                <div className="sticky top-0 z-50 flex items-center justify-between gap-3 border-b border-[#ffd700]/40 bg-[#fff8d6] px-4 py-2.5 text-xs sm:text-sm" data-testid="preview-mode-banner">
                    <div className="flex min-w-0 items-center gap-2 text-[#0a1628]">
                        <Eye className="h-4 w-4 shrink-0 text-[#0a1628]" />
                        <strong className="shrink-0">{tr40("Preview mode")}</strong>
                        <span className="hidden truncate text-[#1a3a5c]/80 sm:inline">{tr40("— this is exactly how parents will see your page. Apply and Book buttons are disabled here.")}</span>
                    </div>
                    <Button size="sm" variant="outline" className="h-8 shrink-0 rounded-lg" onClick={() => { try { window.close(); } catch { /* ignore */ } }} data-testid="preview-close-button"> {tr40("Close")} </Button>
                </div>
            )}
            {/* Top header */}
            <header className="sticky top-0 z-30 sd-glass border-b">
                <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
                    <div className="flex items-center gap-3">
                        {coach.logo_url ? (
                            <img src={coach.logo_url} alt={coach.display_name} className="h-8 w-8 rounded-md object-cover" />
                        ) : (
                            <Logo size="sm" />
                        )}
                        <div className="hidden sm:block text-sm text-[#1a3a5c]">{coach.display_name}</div>
                    </div>
                    <div className="flex items-center gap-2">
                        {/* Returning-parent login — same visual weight as Apply so it
                            reads as a real button, but in a neutral outline style so the
                            CTA hierarchy still pushes new parents toward Apply.
                            "Log in" text is always visible (no icon-only mobile state). */}
                        <Button
                            asChild
                            variant="outline"
                            className="sd-focus h-10 rounded-xl border-2 border-[#0a1628] bg-white px-4 text-sm font-semibold text-[#0a1628] hover:bg-[#0a1628] hover:text-white"
                            data-testid="coach-public-parent-login-link"
                        >
                            <Link to={`/login?from=${encodeURIComponent(`/coach/${slug}`)}`} aria-label={tr40("Log in to your parent account")}>
                                <LogIn className="mr-1 h-4 w-4" /> {tr40("Log in")} </Link>
                        </Button>
                        <Button onClick={() => startRegister()} disabled={enrollCtaDisabled} className="sd-cta sd-focus h-10 rounded-xl px-4" data-testid="coach-register-now-button">
                            {enrollCtaText}
                        </Button>
                    </div>
                </div>
            </header>

            {/* === PRO Landing Page (only renders when instructor is on Pro plan AND has enabled the page) === */}
            {lp && (
                <ProLandingSections lp={lp} primary={primary} coach={coach} onApply={startRegister} />
            )}

            {/* Hero */}
            <section className="sd-noise relative overflow-hidden">
                <div className="sd-gradient-hero absolute inset-0 -z-10" />
                <div className="mx-auto grid max-w-6xl gap-8 px-4 py-12 sm:px-6 lg:grid-cols-[1fr_auto] lg:py-20">
                    <div>
                        <Badge className="mb-4 bg-[#0a1628] text-white hover:bg-[#0a1628]"><ShieldCheck className="mr-1 h-3 w-3" /> {tr40("SplashPad Verified Coach")}</Badge>
                        <h1 className="font-display text-4xl font-bold leading-tight text-[#0a1628] sm:text-5xl">
                            {coach.display_name}
                        </h1>
                        {coach.bio && <p className="mt-4 max-w-2xl text-base text-[#1a3a5c]">{coach.bio}</p>}
                        <div className="mt-5 flex flex-wrap items-center gap-3 text-sm text-[#1a3a5c]">
                            {coach.pool_name && <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" /> {coach.pool_name}</span>}
                            {coach.service_area && <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" /> {coach.service_area}</span>}
                            {coach.years_experience > 0 && <span className="inline-flex items-center gap-1"><Award className="h-4 w-4" /> {coach.years_experience}{tr40("+ yrs experience")}</span>}
                        </div>
                        {coach.certifications?.length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-2">
                                {coach.certifications.map((c) => (
                                    <Badge key={c} variant="outline" className="border-[#1a3a5c]/20 bg-white text-[#1a3a5c]">{c}</Badge>
                                ))}
                            </div>
                        )}
                        <div className="mt-7 flex flex-wrap items-center gap-3">
                            <Button onClick={() => startRegister()} disabled={enrollCtaDisabled} className="sd-focus h-12 rounded-xl px-6 text-base" style={{ background: primary, color: '#0a1628', fontWeight: 700 }} data-testid="hero-register-button">
                                {enrollCtaText} <ArrowRight className="ml-1 h-4 w-4" />
                            </Button>
                            {contactVisible && (contactPhoneClean || (coach.settings?.accept_inquiries && coach.contact_email)) && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setContactDialogOpen(true)}
                                    className="h-12 rounded-xl border-[#1a3a5c]/20 px-6"
                                    data-testid="contact-coach-button"
                                >
                                    <Phone className="mr-1 h-4 w-4" /> {contactText}
                                </Button>
                            )}
                        </div>
                        {/* Social media — icons hidden entirely if the coach hasn't set any URL. */}
                        <SocialIconLinks
                            coach={coach}
                            size="md"
                            testIdPrefix="coach-public-social"
                            className="mt-5"
                        />
                    </div>
                    <div className="hidden lg:flex items-start">
                        {coach.photo_url ? (
                            <img src={coach.photo_url} alt={coach.display_name} className="h-64 w-64 rounded-2xl object-cover shadow-[0_18px_45px_rgba(10,22,40,0.12)]" />
                        ) : (
                            <div className="h-64 w-64 rounded-2xl bg-gradient-to-br from-[#00d4ff]/30 to-[#1a3a5c]/30 flex items-center justify-center text-[#0a1628] font-display text-5xl font-bold">
                                {(coach.display_name || 'SC').slice(0, 2).toUpperCase()}
                            </div>
                        )}
                    </div>
                </div>
            </section>

            <WaveDivider from="#ffffff" to="#f4f7fb" />

            {/* Packages — Pro lead-magnet replacement OR Class Finder wizard OR classic list */}
            {(lp && lp.show_packages === false) ? (
                <HiddenPackagesLeadMagnet
                    lp={lp}
                    coach={coach}
                    onApply={() => startRegister()}
                />
            ) : showWizardPreview ? (
                <>
                    <div className="bg-[#f4f7fb] pt-6" data-testid="classfinder-preview-note">
                        <div className="mx-auto max-w-6xl px-4 sm:px-6">
                            <div className="flex items-center gap-2 rounded-xl border border-[#ffd700]/50 bg-[#fff8d6] px-4 py-2.5 text-xs font-semibold text-[#0a1628]">
                                <Eye className="h-4 w-4 shrink-0" /> {tr40("Preview only — parents see this Class Finder inside their private portal after their application is approved. It never shows on your public page.")} </div>
                        </div>
                    </div>
                    <ClassFinderWizard
                        slug={slug}
                        coach={coach}
                        packages={packages}
                        programs={programs}
                        locations={coach.locations || []}
                        primary={primary}
                        previewMode={isPreview || isParentPreview}
                    />
                </>
            ) : (
                <section id="packages" className="bg-[#f4f7fb] py-12 lg:py-20">
                    <div className="mx-auto max-w-6xl px-4 sm:px-6">
                        <h2 className="font-display text-2xl font-bold text-[#0a1628] sm:text-3xl">{tr40("Lesson packages")}</h2>
                        <p className="mt-1 text-[#1a3a5c]/80">{tr40("Pick a package and we’ll guide you through the rest.")}</p>
                        {packages.length === 0 ? (
                            <Card className="sd-card mt-6 p-8 text-center text-[#1a3a5c]">{tr40("No packages are currently available.")}</Card>
                        ) : (
                            <div className="mt-6 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                                {packages.map((p) => {
                                    // Phase 67 — Scott Metcalf feedback. Render each
                                    // configured class_slot as its own schedule row so
                                    // parents can see EXACTLY which days/times/dates a
                                    // package runs. A package with two time slots no
                                    // longer collapses into a single misleading "10
                                    // sessions" line — each slot shows its own session
                                    // count in the context of the date range.
                                    const slots = Array.isArray(p.class_slots) ? p.class_slots : [];
                                    const hasSchedule = slots.length > 0;
                                    return (
                                    <Card key={p.id} className="sd-card sd-card-hover relative flex flex-col p-6" data-testid={`coach-package-card-${p.id}`}>
                                        {p.is_popular && (
                                            <Badge className="absolute -top-2 right-4 bg-[#ffd700] text-[#0a1628] hover:bg-[#ffd700]">{tr40("Most Popular")}</Badge>
                                        )}
                                        <h3 className="font-display text-lg font-bold text-[#0a1628]">
                                            {p.name}
                                            {p.package_kind === 'camp' && <Badge className="ml-2 align-middle bg-[#00d4ff]/15 text-[#0a1628] hover:bg-[#00d4ff]/15" data-testid={`coach-package-camp-badge-${p.id}`}><Tent className="mr-1 h-3 w-3" /> {tr40("Camp")}</Badge>}
                                        </h3>
                                        <div className="mt-1 flex items-baseline gap-1">
                                            <span className="text-3xl font-bold text-[#0a1628]">{fmtPrice(p.price_cents, p?.currency)}</span>
                                            {p.package_type === 'subscription' && (
                                                <span className="text-xs text-[#1a3a5c]/70">/ {p.billing_interval}</span>
                                            )}
                                        </div>
                                        {p.package_type === 'subscription' && (
                                            <Badge className="mt-2 bg-[#1a3a5c] text-white hover:bg-[#1a3a5c]">{tr40("Subscription · auto-renews")} {p.billing_interval}{t42("ly")}</Badge>
                                        )}
                                        <p className="mt-2 text-sm text-[#1a3a5c]">{p.description}</p>
                                        <ul className="mt-4 space-y-1 text-sm text-[#1a3a5c]">
                                            {/* When the coach has NOT defined class_slots we keep the
                                                legacy aggregate "{N} sessions · {dur} min" line so
                                                older packages still render exactly as before. */}
                                            {!hasSchedule && (
                                                <li className="flex items-center gap-2" data-testid={`pkg-${p.id}-aggregate-sessions`}>
                                                    <CheckCircle2 className="h-4 w-4 text-[#00d4ff]" /> {p.session_count} {tr40("sessions ·")} {p.duration_minutes} {t42("min")} </li>
                                            )}
                                            {/* Phase 80 — flexible packages: set the expectation that
                                                the coach schedules lesson times with each family. */}
                                            {!hasSchedule && p.flexible_schedule && (
                                                <li className="flex items-center gap-2" data-testid={`pkg-${p.id}-flexible-note`}>
                                                    <CheckCircle2 className="h-4 w-4 text-[#00d4ff]" /> {tr40("Flexible schedule — coach sets times with you")} </li>
                                            )}
                                            <li className="flex items-center gap-2"><Users className="h-4 w-4 text-[#00d4ff]" /> {p.age_group} · {p.swim_level}</li>
                                        </ul>
                                        {/* Phase 116a — teaching coach (name + photo) before enrolling */}
                                        {p.teaching_coach && (
                                            <div className="mt-3">
                                                <CoachChip coach={p.teaching_coach} size="sm" label={tr40("Coach")} testId={`coach-package-coach-${p.id}`} />
                                            </div>
                                        )}
                                        {hasSchedule && (
                                            <div
                                                className="mt-4 rounded-lg border border-[#1a3a5c]/10 bg-white/60 p-3 text-sm text-[#1a3a5c]"
                                                data-testid={`pkg-${p.id}-schedule`}
                                            >
                                                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[#1a3a5c]/70">
                                                    {slots.length > 1 ? tr40("Available class times") : tr40("Class schedule")}
                                                </div>
                                                <ul className="space-y-1.5">
                                                    {slots.map((s, idx) => {
                                                        const summary = formatSlotSchedule(s, p);
                                                        const sessions = countSlotSessions(s, p);
                                                        const duration = Number(s.duration_minutes) || Number(p.duration_minutes) || 0;
                                                        const label = (s.label || '').trim();
                                                        return (
                                                            <li
                                                                key={s.id || idx}
                                                                className="flex items-start gap-2"
                                                                data-testid={`pkg-${p.id}-slot-${s.id || idx}`}
                                                            >
                                                                <CalendarRange className="mt-0.5 h-4 w-4 shrink-0 text-[#00d4ff]" />
                                                                <div className="min-w-0 flex-1">
                                                                    {label && (
                                                                        <div className="font-semibold text-[#0a1628]">{label}</div>
                                                                    )}
                                                                    <div className="font-medium text-[#0a1628]">{summary || '—'}</div>
                                                                    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-[#1a3a5c]/80">
                                                                        {sessions !== null && sessions > 0 && (
                                                                            <span>{sessions} {sessions === 1 ? tr40("lesson") : tr40("lessons")}</span>
                                                                        )}
                                                                        {sessions === null && (
                                                                            <span>{tr40("Ongoing")}</span>
                                                                        )}
                                                                        {duration > 0 && (
                                                                            <>
                                                                                <span aria-hidden>·</span>
                                                                                <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {duration} {t42("min")}</span>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                                {slots.length > 1 && (
                                                    <div className="mt-2 text-[11px] text-[#1a3a5c]/70"> {tr40("You'll pick your class time next.")} </div>
                                                )}
                                            </div>
                                        )}
                                        <div className="mt-3">
                                            {p.payment_method === 'manual_invoice' && <Badge variant="outline" className="border-[#1a3a5c]/20 bg-white text-[#1a3a5c]">{tr40("Coach will invoice")}</Badge>}
                                            {p.payment_method === 'external_link' && <PaymentProviderBadge provider={p.payment_link_provider} />}
                                            {p.payment_method === 'native_stripe' && <Badge variant="outline" className="border-[#1a3a5c]/20 bg-white text-[#1a3a5c]">{tr40("Pay securely")}</Badge>}
                                        </div>
                                        <Button onClick={() => startRegister(p.id)} disabled={enrollCtaDisabled} className="sd-focus mt-5" style={{ background: primary, color: '#0a1628', fontWeight: 700 }} data-testid={`enroll-button-${p.id}`}>
                                            {alreadyApproved ? tr40("Enroll") : (applicationPending ? tr40("Application under review") : tr40("Apply Now"))}
                                        </Button>
                                    </Card>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* How It Works */}
            <section className="bg-white py-12 lg:py-20">
                <div className="mx-auto max-w-6xl px-4 sm:px-6">
                    <h2 className="font-display text-2xl font-bold text-[#0a1628] sm:text-3xl">{tr40("How it works")}</h2>
                    <div className="mt-6 grid gap-5 md:grid-cols-3">
                        {[
                            { n: '01', t: 'Create your account', d: 'Quick signup so we can save your info and waiver.' },
                            { n: '02', t: 'Pick a package', d: 'Choose the right package for your child.' },
                            { n: '03', t: 'Book your lesson', d: 'Sign the waiver, complete payment, and you’re in!' },
                        ].map((s) => (
                            <Card key={s.n} className="sd-card sd-card-hover p-6">
                                <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl" style={{ background: '#0a1628', color: '#ffd700' }}>{s.n}</div>
                                <h3 className="font-display text-lg font-bold text-[#0a1628]">{s.t}</h3>
                                <p className="mt-2 text-sm text-[#1a3a5c]">{s.d}</p>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* Locations — Phase 107 (Pro coaches): clickable addresses with
                Google/Apple Maps directions so families can see how far away
                each pool is before applying. */}
            {coach.is_pro && (coach.locations || []).length > 0 && (
                <section className="bg-[#f4f7fb] py-12 lg:py-16" data-testid="coach-public-locations-section">
                    <div className="mx-auto max-w-6xl px-4 sm:px-6">
                        <LocationCards
                            locations={coach.locations}
                            subtitle={t42("Tap a map button for directions in your map of choice.")}
                        />
                    </div>
                </section>
            )}

            {/* Testimonials */}
            {testimonials?.length > 0 && (
                <>
                    <WaveDivider from="#ffffff" to="#f4f7fb" />
                    <section className="bg-[#f4f7fb] py-12 lg:py-20">
                        <div className="mx-auto max-w-6xl px-4 sm:px-6">
                            <h2 className="font-display text-2xl font-bold text-[#0a1628] sm:text-3xl">{tr40("What parents say")}</h2>
                            <div className="mt-6 grid gap-5 md:grid-cols-3">
                                {testimonials.map((t) => (
                                    <Card key={t.id} className="sd-card p-6">
                                        <div className="mb-3 flex items-center gap-1 text-[#ffd700]">
                                            {[1,2,3,4,5].map(s => <Star key={s} className="h-4 w-4 fill-[#ffd700]" />)}
                                        </div>
                                        <p className="text-[#1a3a5c]">“{t.text}”</p>
                                        <div className="mt-3 text-sm font-semibold text-[#0a1628]">{t.parent_name}</div>
                                        {t.student_name && <div className="text-xs text-[#1a3a5c]/70">{tr40("Parent of")} {t.student_name}</div>}
                                    </Card>
                                ))}
                            </div>
                        </div>
                    </section>
                </>
            )}

            {/* Google Reviews badge — shown on the Starter public page when a Google Business URL is connected.
                The Pro landing page (rendered above) has its own themed Google Reviews block. */}
            {coach.google_business_url && !lp && (
                <section className="bg-white py-12 lg:py-16">
                    <div className="mx-auto max-w-4xl px-4 sm:px-6">
                        <GoogleReviewsBadge url={coach.google_business_url} />
                    </div>
                </section>
            )}

            {/* FAQs */}
            {faqs?.length > 0 && (
                <section className="bg-white py-12 lg:py-20">
                    <div className="mx-auto max-w-3xl px-4 sm:px-6">
                        <h2 className="font-display text-2xl font-bold text-[#0a1628] sm:text-3xl">{tr40("Frequently asked questions")}</h2>
                        <Accordion type="single" collapsible className="mt-4">
                            {faqs.map((f) => (
                                <AccordionItem key={f.id} value={f.id}>
                                    <AccordionTrigger className="text-left">{f.question}</AccordionTrigger>
                                    <AccordionContent className="text-[#1a3a5c]">{f.answer}</AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>
                </section>
            )}

            {/* Sticky mobile CTA */}
            <div className="sticky bottom-0 z-30 border-t bg-white p-3 sm:hidden">
                <Button onClick={() => startRegister()} disabled={enrollCtaDisabled} className="sd-cta sd-focus w-full h-11 rounded-xl" data-testid="sticky-mobile-register-button">
                    {enrollCtaText}
                </Button>
            </div>

            {/* Footer */}
            <footer className="bg-[#0a1628] py-8 text-white">
                <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-4 text-sm sm:px-6 md:flex-row">
                    <Logo light />
                    <div className="text-white/60">{tr40("Powered by SplashPad · SSA MEDIA LLC")}</div>
                    <div className="flex items-center gap-3 text-white/70">
                        <Link to="/privacy" className="hover:text-white" data-testid="coachpublic-privacy-link">{tr40("Privacy")}</Link>
                        <span aria-hidden>·</span>
                        <Link to="/terms" className="hover:text-white" data-testid="coachpublic-terms-link">{tr40("Terms")}</Link>
                    </div>
                </div>
            </footer>

            {/* Phase 73j — Contact dialog (replaces silent mailto:/tel: fallbacks).
                We mount once at the bottom so any in-page "Contact Coach"
                trigger (hero, sticky CTA, etc.) can open it via setContactDialogOpen. */}
            <ContactCoachDialog
                open={contactDialogOpen}
                onOpenChange={setContactDialogOpen}
                coach={coach}
                primary={primary}
            />
            {announcementNode}
        </div>
    );
}


// ============================================================================
// Pro-tier Landing Page Sections — rendered above the standard public page
// when the instructor has published their custom landing page.
// ============================================================================

function getYouTubeEmbedUrl(url) {
    if (!url) return null;
    try {
        const u = new URL(url);
        if (u.hostname.includes('youtube.com')) {
            const id = u.searchParams.get('v');
            return id ? `https://www.youtube.com/embed/${id}` : null;
        }
        if (u.hostname === 'youtu.be') {
            const id = u.pathname.replace(/^\//, '');
            return id ? `https://www.youtube.com/embed/${id}` : null;
        }
        if (u.hostname.includes('vimeo.com')) {
            const id = u.pathname.split('/').filter(Boolean).pop();
            return id ? `https://player.vimeo.com/video/${id}` : null;
        }
    } catch (err) {
        /* invalid url — skip; just don't render the embed */
        if (process.env.NODE_ENV !== 'production') console.debug('getYouTubeEmbedUrl: invalid url', err);
    }
    return null;
}

// ============================================================================
// Hidden-packages lead-magnet block — renders when a Pro coach has turned off
// the packages section. Encourages parents to "apply for pricing & details"
// instead of seeing prices upfront.
// ============================================================================
function HiddenPackagesLeadMagnet({ lp, coach, onApply }) {
    const { t: tr40 } = useLocaleR40();
    // resolveTheme() handles the custom theme + accent override in one place.
    // Pass the saved accent_color through so the CTA color matches the editor preview.
    const accentOverride = lp.accent_color && lp.accent_color.toLowerCase() !== '#00d4ff' ? lp.accent_color : null;
    const theme = resolveTheme(lp.theme, lp.custom_hero_color, accentOverride);
    const font = getFont(lp.font_family);
    const accent = theme.accent;
    const accentText = theme.accentText;
    const headline = lp.hidden_packages_headline || `Custom packages, tailored for ${coach?.display_name || 'you'}`;
    const message = lp.hidden_packages_message || "Every swimmer is different. Apply now and I'll personally review your goals and reach out within 24 hours with a recommended plan and pricing.";
    const photos = (lp.hidden_packages_photos || []).slice(0, 4);

    return (
        <section className="py-14 lg:py-20" style={{ background: theme.sectionAlt }} data-testid="lp-hidden-packages-block">
            <div className="mx-auto max-w-6xl px-4 sm:px-6">
                <div className="grid items-center gap-8 md:grid-cols-2">
                    <div>
                        <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider" style={{ background: accent, color: accentText }}> {tr40("✦ Apply for pricing")} </div>
                        <h2 className={`${font.headingClass} mt-4 text-3xl font-bold leading-tight sm:text-4xl`} style={{ color: theme.textPrimary }}>
                            {headline}
                        </h2>
                        <p className="mt-4 text-base leading-relaxed" style={{ color: theme.textSecondary }}>
                            {message}
                        </p>
                        <Button
                            onClick={() => onApply()}
                            className="sd-focus mt-6 h-12 rounded-xl px-6 text-base font-bold shadow-lg"
                            style={{ background: accent, color: accentText }}
                            data-testid="lp-hidden-packages-cta"
                        >
                            {lp.cta_text || 'Apply for Lessons'} <ArrowRight className="ml-1 h-4 w-4" />
                        </Button>
                        {(coach?.phone || coach?.contact_email) && (
                            <p className="mt-3 text-sm" style={{ color: theme.textSecondary }}> {tr40("Prefer to chat?")} {coach?.phone && (<>{tr40("Call")} <a href={`tel:${coach.phone}`} className="font-semibold underline">{coach.phone}</a></>)}
                                {coach?.phone && coach?.contact_email && ' · '}
                                {coach?.contact_email && (<>{tr40("Email")} <a href={`mailto:${coach.contact_email}`} className="font-semibold underline">{coach.contact_email}</a></>)}
                            </p>
                        )}
                    </div>

                    {/* Photo collage (1–4 photos). Layout adapts to count. */}
                    {photos.length > 0 ? (
                        <div className={`grid gap-3 ${photos.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                            {photos.map((url, i) => (
                                <div
                                    key={url}
                                    className={`overflow-hidden rounded-2xl border bg-white shadow-sm ${photos.length === 3 && i === 0 ? 'row-span-2 aspect-[3/4]' : 'aspect-square'}`}
                                >
                                    <img src={url} alt="" loading="lazy" className="h-full w-full object-cover" />
                                </div>
                            ))}
                        </div>
                    ) : (
                        // Tasteful fallback when no photos: a decorative gradient panel matching the theme
                        <div
                            className="aspect-[4/3] rounded-3xl border shadow-sm"
                            style={{ background: theme.heroGradient }}
                        />
                    )}
                </div>
            </div>
        </section>
    );
}

function ProLandingSections({ lp, primary, coach, onApply }) {
    const { t: tr40 } = useLocaleR40();
    // resolveTheme() handles BOTH the custom solid-color theme AND the
    // optional CTA accent override in one place, so the editor's mini-preview
    // and the live public page draw from identical logic.
    const accentOverride = lp.accent_color && lp.accent_color.toLowerCase() !== '#00d4ff' ? lp.accent_color : null;
    const theme = resolveTheme(lp.theme, lp.custom_hero_color, accentOverride);
    const font = getFont(lp.font_family);
    const accent = theme.accent;
    const accentText = theme.accentText;
    const heroPhoto = lp.hero_photo_url;
    const cta = lp.cta_text || 'Apply for Lessons';

    const headingClass = `${font.headingClass} font-bold leading-tight`;

    return (
        <div data-testid="pro-landing-page" className={font.bodyClass}>
            {/* === Custom HERO === */}
            <section
                className="relative overflow-hidden"
                style={!heroPhoto ? { background: theme.heroGradient } : undefined}
            >
                {heroPhoto && (
                    <div className="absolute inset-0">
                        <img src={heroPhoto} alt="" className="h-full w-full object-cover" />
                        {/* Dark overlay ALWAYS — guarantees white text reads regardless of photo brightness */}
                        <div className="absolute inset-0" style={{ background: theme.heroOverlay }} />
                    </div>
                )}
                <div className="relative z-10 mx-auto max-w-5xl px-4 py-20 text-center sm:px-6 sm:py-28 lg:py-36">
                    {lp.hero_headline && (
                        <h1 className={`${headingClass} text-4xl text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.45)] sm:text-5xl lg:text-6xl`}>
                            {lp.hero_headline}
                        </h1>
                    )}
                    {lp.hero_subheadline && (
                        <p className="mx-auto mt-5 max-w-2xl text-base text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)] sm:text-lg">
                            {lp.hero_subheadline}
                        </p>
                    )}
                    <Button
                        onClick={() => onApply()}
                        className="sd-focus mt-8 h-14 rounded-xl px-8 text-base font-bold shadow-lg"
                        style={{ background: accent, color: accentText }}
                        data-testid="lp-public-cta-hero"
                    >
                        {cta} <ArrowRight className="ml-1 h-4 w-4" />
                    </Button>
                    {/* Social media icons — only renders platforms the coach filled in.
                        Wrapped in a centering flex so the row stays centered under
                        the CTA on the custom Pro hero. */}
                    <div className="mt-6 flex justify-center">
                        <SocialIconLinks
                            coach={coach}
                            size="md"
                            testIdPrefix="coach-public-hero-social"
                        />
                    </div>
                </div>
            </section>

            {/* === ABOUT ===
                Only render this when the coach DIDN'T also fill in their profile bio.
                Otherwise the bio shows up twice (here + in the default Hero below),
                and the agreed behaviour is to keep the lower, default-Hero bio. */}
            {lp.about_html && !coach.bio && (
                <section className="bg-white py-14 lg:py-20" style={{ background: theme.sectionBg }}>
                    <div className="mx-auto max-w-3xl px-4 sm:px-6">
                        <h2 className={`${headingClass} text-3xl`} style={{ color: theme.textPrimary }}>{tr40("About")} {coach.display_name}</h2>
                        <div className="prose prose-lg mt-5 max-w-none whitespace-pre-line" style={{ color: theme.textSecondary }}>
                            {lp.about_html}
                        </div>
                    </div>
                </section>
            )}

            {/* === GALLERY === */}
            {lp.gallery_photos?.length > 0 && (
                <section className="py-14 lg:py-20" style={{ background: theme.sectionAlt }}>
                    <div className="mx-auto max-w-6xl px-4 sm:px-6">
                        <h2 className={`${headingClass} text-3xl`} style={{ color: theme.textPrimary }}>{tr40("In the water")}</h2>
                        <p className="mt-2" style={{ color: theme.textSecondary }}>{tr40("A glimpse into our lessons.")}</p>
                        <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                            {lp.gallery_photos.map((url, i) => (
                                <div key={url} className="aspect-square overflow-hidden rounded-2xl border bg-white">
                                    <img src={url} alt={`Gallery ${i + 1}`} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 hover:scale-105" />
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* === VIDEOS === */}
            {lp.video_urls?.length > 0 && (
                <section className="py-14 lg:py-20" style={{ background: theme.sectionBg }}>
                    <div className="mx-auto max-w-5xl px-4 sm:px-6">
                        <h2 className={`${headingClass} text-3xl`} style={{ color: theme.textPrimary }}>{tr40("Watch us in action")}</h2>
                        <div className={`mt-8 grid gap-6 ${lp.video_urls.length > 1 ? 'md:grid-cols-2' : ''}`}>
                            {lp.video_urls.map((vu, i) => {
                                const embed = getYouTubeEmbedUrl(vu);
                                if (!embed) return null;
                                return (
                                    <div key={vu} className="aspect-video overflow-hidden rounded-2xl border bg-black shadow-md">
                                        <iframe
                                            src={embed}
                                            title={`Video ${i + 1}`}
                                            loading="lazy"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                            className="h-full w-full"
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            {/* === REVIEWS / TESTIMONIALS ===
                Pro coaches pick where reviews come from via the editor:
                  - "manual" → only the hand-typed testimonials
                  - "google" → only the Google badge (handled below)
                  - "both"   → manual + Google badge stacked */}
            {lp.testimonials?.length > 0 && (lp.reviews_source || 'manual') !== 'google' && (
                <section className="py-14 text-white lg:py-20" style={{ background: theme.heroGradient }}>
                    <div className="mx-auto max-w-5xl px-4 sm:px-6">
                        <h2 className={`${headingClass} text-3xl`}>{tr40("What parents are saying")}</h2>
                        <div className="mt-8 grid gap-6 md:grid-cols-2">
                            {lp.testimonials.map((t) => (
                                <Card key={`${t.name || 'anon'}|${(t.quote || '').slice(0, 32)}`} className="border-0 bg-white/10 p-6 backdrop-blur">
                                    <div className="mb-3 flex">
                                        {[1, 2, 3, 4, 5].map((n) => (
                                            <Star key={n} className={`h-4 w-4 ${(t.rating || 5) >= n ? 'fill-[#ffd700] text-[#ffd700]' : 'text-white/20'}`} />
                                        ))}
                                    </div>
                                    {t.quote && <p className="text-lg leading-relaxed text-white/95">"{t.quote}"</p>}
                                    <div className="mt-4 text-sm text-white/70">
                                        <strong className="text-white">{t.name}</strong>
                                        {t.location && <span> · {t.location}</span>}
                                    </div>
                                </Card>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* === GOOGLE REVIEWS (Pro) ===
                Auto-displayed when the coach picked "google" or "both" AND has
                connected their Google Business profile via Marketing tab. */}
            {coach.google_business_url && ['google', 'both'].includes(lp.reviews_source || 'manual') && (
                <section className="bg-white py-12 lg:py-16">
                    <div className="mx-auto max-w-4xl px-4 sm:px-6">
                        <GoogleReviewsBadge
                            url={coach.google_business_url}
                            coachName={coach.display_name}
                            accentColor={accent}
                        />
                    </div>
                </section>
            )}

            {/* === Phase 66 Milestone 2 — CUSTOM ADD-ON SECTIONS === */}
            {Array.isArray(lp.custom_sections) && lp.custom_sections.length > 0 && (() => {
                // Visibility map: section_visibility[id] === false means hidden.
                const hidden = (id) => (lp.section_visibility || {})[id] === false;
                // Use saved order if present; fall back to insertion order.
                const order = Array.isArray(lp.section_order) ? lp.section_order : [];
                const byId = new Map(lp.custom_sections.map((s) => [s.id, s]));
                const orderedCustomSections = [];
                // First, sections that appear in the saved order.
                order.forEach((id) => {
                    if (byId.has(id) && !hidden(id)) {
                        orderedCustomSections.push(byId.get(id));
                        byId.delete(id);
                    }
                });
                // Then any custom sections missing from saved_order (appended at end).
                byId.forEach((s) => {
                    if (!hidden(s.id)) orderedCustomSections.push(s);
                });
                return orderedCustomSections.map((s) => (
                    <CustomSectionRenderer
                        key={s.id}
                        section={s}
                        coach={coach}
                        accent={accent}
                        accentText={accentText}
                        headingClass={headingClass}
                        onApply={onApply}
                    />
                ));
            })()}

            {/* === MID-PAGE CTA === */}
            <section className="py-12" style={{ background: theme.sectionAlt }}>
                <div className="mx-auto max-w-3xl px-4 text-center sm:px-6">
                    <h3 className={`${headingClass} text-2xl`} style={{ color: theme.textPrimary }}>{tr40("Ready to dive in?")}</h3>
                    {/* Phase 76d — copy branches on whether the coach uses
                        Instant Signup. Prior wording told every parent to
                        expect a 24-48h wait even when they were about to be
                        auto-approved, which caused abandonment. */}
                    <p className="mt-2 text-sm" style={{ color: theme.textSecondary }}>
                        {coach.settings?.auto_approve_applications
                            ? tr40("Sign up in under a minute — instant approval, no waiting for review.")
                            : tr40("Apply now — your coach will review your application and get back to you within 24-48 hours.")}
                    </p>
                    <Button
                        onClick={() => onApply()}
                        className="sd-focus mt-5 h-12 rounded-xl px-6 text-base font-bold"
                        style={{ background: accent, color: accentText }}
                        data-testid="lp-public-cta-mid"
                    >
                        {cta} <ArrowRight className="ml-1 h-4 w-4" />
                    </Button>
                </div>
            </section>
        </div>
    );
}


// ============================================================================
// Google Reviews Badge — shown when an instructor connects their Google Business
// profile via Instructor → Marketing. Acts as social proof + CTA to read the
// full reviews on Google. Replaces the need for manually entered testimonials.
// ============================================================================
function GoogleReviewsBadge({ url, coachName, accentColor }) {
    const { t: t42 } = useLocaleR42();
    const { t: tr40 } = useLocaleR40();
    const accent = accentColor || '#00d4ff';
    return (
        <div
            className="relative overflow-hidden rounded-3xl border bg-white p-6 shadow-sm sm:p-8"
            data-testid="lp-google-reviews-badge"
        >
            <div className="grid items-center gap-6 md:grid-cols-[auto_1fr_auto]">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-[#f8fafc] ring-1 ring-[#1a3a5c]/10 shadow-sm">
                    {/* Real Google "G" using authentic 4-color logo. */}
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-8 w-8" aria-label={t42("Google")}>
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                        <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z" />
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
                    </svg>
                </div>
                <div>
                    <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-display text-xl font-bold text-[#0a1628] sm:text-2xl">
                            {coachName ? `${coachName} on Google` : tr40("See us on Google")}
                        </h3>
                        <span className="inline-flex items-center gap-1 rounded-full bg-[#e6fbff] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#0a1628]">
                            <ShieldCheck className="h-3 w-3" /> {tr40("Verified business")} </span>
                    </div>
                    <div className="mt-1 flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((n) => (
                            <Star key={n} className="h-5 w-5 fill-[#ffd700] text-[#ffd700]" />
                        ))}
                        <span className="ml-2 text-sm font-semibold text-[#0a1628]">{tr40("5-star rated")}</span>
                    </div>
                    <p className="mt-2 text-sm text-[#1a3a5c]"> {tr40("Read what parents are saying on Google — independent reviews from real families.")} </p>
                </div>
                <Button
                    asChild
                    className="sd-focus h-11 w-full rounded-xl px-5 text-sm font-bold md:w-auto"
                    style={{ background: accent, color: '#0a1628' }}
                    data-testid="lp-google-reviews-cta"
                >
                    <a href={url} target="_blank" rel="noopener noreferrer"> {tr40("Read reviews on Google")} <ArrowRight className="ml-1 h-4 w-4" />
                    </a>
                </Button>
            </div>
        </div>
    );
}
